/**
 * Meta (Facebook) Pixel — env-driven, init-only bootstrap.
 *
 * Loads fbevents.js and calls fbq('init', <id>) so the browser sets the
 * `_fbp` / `_fbc` cookies and enables advanced matching. We deliberately do
 * NOT call fbq('track', 'PageView') here: every event flows through our
 * server-side CAPI relay (POST /analytics/event), which is the single source
 * of truth. Firing a browser PageView too would double-count.
 *
 * IMPORTANT: we also disable `autoConfig` BEFORE init. With autoConfig on (the
 * default), fbevents.js automatically sends a browser `PageView` on init — which
 * collides with our server-relay PageView and double-counts. autoConfig=false
 * stops ALL pixel-automatic browser events, leaving the relay as the only sender.
 * We don't lose match quality: user data (phone/_fbp/_fbc) is sent server-side
 * via the relay, not via browser automatic advanced matching.
 *
 * The Pixel ID comes from `VITE_META_PIXEL_ID` and MUST equal the backend's
 * `META_PIXEL_ID`. No-op when the env var is unset.
 */

declare global {
    interface Window {
        fbq?: ((...args: unknown[]) => void) & {
            callMethod?: (...args: unknown[]) => void;
            queue?: unknown[];
            loaded?: boolean;
            version?: string;
            push?: unknown;
        };
        _fbq?: unknown;
    }
}

let initialized = false;

export function initMetaPixel(): void {
    if (initialized) return;
    // SSR / prerender safe — only run in a real browser.
    if (typeof window === "undefined" || typeof document === "undefined") return;

    const pixelId = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
    if (!pixelId) {
        if (import.meta.env.DEV) {
            console.warn(
                "[metaPixel] VITE_META_PIXEL_ID not set — Meta Pixel disabled.",
            );
        }
        return;
    }

    /* Standard Meta Pixel loader — adapted to init only (no auto PageView). */
    /* eslint-disable */
    (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
        if (f.fbq) return;
        n = f.fbq = function () {
            n.callMethod
                ? n.callMethod.apply(n, arguments)
                : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
    })(
        window,
        document,
        "script",
        "https://connect.facebook.net/en_US/fbevents.js",
    );
    /* eslint-enable */

    // Disable pixel-automatic browser events (incl. the auto PageView) so the
    // server-side relay is the single source of truth — prevents double-counting.
    window.fbq?.("set", "autoConfig", false, pixelId);
    window.fbq?.("init", pixelId);
    initialized = true;
}
